import { useEffect, useState } from 'react';
import { ArrowRight, FlaskConical, KeyRound, Loader2, LockKeyhole, Mail, Moon, Sun } from 'lucide-react';
import type { OtpMethod, Theme } from '@itr/shared';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../theme/ThemeProvider';
import { ApiError, apiUrl, isDesktopShell, server } from '../api/client';

export function Login() {
  const { login, demo, enterDemo } = useAuth();
  const { theme, resolvedMode, setMode, preference } = useTheme();
  // On a demo site the logins are public and offered as buttons; the form
  // below still works, and starts filled in with the first of them.
  const [email, setEmail] = useState(demo?.logins[0]?.email ?? '');
  const [password, setPassword] = useState(demo?.logins[0]?.password ?? '');
  const [entering, setEntering] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [branding, setBranding] = useState<Theme['branding']>(theme.branding);

  // Set once a good password comes back needing a second factor — the form
  // then shows the code step instead of restarting from scratch.
  const [challenge, setChallenge] = useState<{ methods: OtpMethod[]; method: OtpMethod; sentTo?: string } | null>(null);
  const [code, setCode] = useState('');

  // The desktop app is not served by the server, so it has to be told where the
  // server is. A browser defaults to same-origin and never shows this field.
  const [serverUrl, setServerUrl] = useState(server.base || (isDesktopShell() ? 'http://localhost:4000' : ''));
  const needsServer = isDesktopShell();

  // Branding comes from a public endpoint so a white-labelled tenant sees their
  // own logo *before* signing in, not after.
  useEffect(() => {
    void fetch(apiUrl('/api/themes/active'))
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { branding?: Theme['branding'] } | null) => {
        if (data?.branding) setBranding(data.branding);
      })
      .catch(() => {});
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (needsServer) server.set(serverUrl.trim());
      const outcome = await login(email.trim(), password);
      if (outcome.status === 'otp_required') {
        setChallenge({ methods: outcome.methods, method: outcome.method, sentTo: outcome.sentTo });
        setBusy(false);
      }
      // status 'ok' needs nothing further — AuthProvider already has the
      // session and the router takes it from here.
    } catch (err) {
      setError(
        err instanceof Error
          ? needsServer && /fetch|network|failed/i.test(err.message)
            ? `Could not reach ${serverUrl.trim() || 'the server'}. Check the address and that the server is running.`
            : err.message
          : 'Sign in failed',
      );
      setBusy(false);
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password, { code: code.trim(), method: challenge.method });
      // 'ok' is the only outcome a code submission can produce — an
      // otp_required response here would mean the server disagreed with its
      // own challenge, which never happens.
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'invalid_otp' ? err.message : err instanceof Error ? err.message : 'Could not verify that code');
      setCode('');
      setBusy(false);
    }
  };

  /** Switches to the other second factor, or re-sends an email code — both are just "ask again with no code yet". */
  const switchMethod = async (method: OtpMethod) => {
    setBusy(true);
    setError(null);
    setCode('');
    try {
      const outcome = await login(email.trim(), password, { code: '', method });
      if (outcome.status === 'otp_required') setChallenge({ methods: outcome.methods, method: outcome.method, sentTo: outcome.sentTo });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a new code');
    } finally {
      setBusy(false);
    }
  };

  const backToPassword = () => {
    setChallenge(null);
    setCode('');
    setError(null);
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background: 'var(--itr-bg)',
        position: 'relative',
      }}
    >
      <button
        className="btn btn-ghost btn-icon"
        onClick={() => setMode(resolvedMode === 'dark' ? 'light' : 'dark')}
        style={{ position: 'absolute', top: 20, right: 20 }}
        aria-label="Toggle colour mode"
        title={`Appearance: ${preference.mode}`}
      >
        {resolvedMode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
      </button>

      <div style={{ width: '100%', maxWidth: demo ? 460 : 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 26 }}>
          {branding.logoUrl ? (
            <img
              src={resolvedMode === 'dark' ? (branding.logoDarkUrl ?? branding.logoUrl) : branding.logoUrl}
              alt=""
              style={{ height: 44, objectFit: 'contain', marginBottom: 14 }}
            />
          ) : (
            <div
              style={{
                width: 46,
                height: 46,
                margin: '0 auto 14px',
                display: 'grid',
                placeItems: 'center',
                borderRadius: 'var(--itr-radius)',
                background: 'var(--itr-primary)',
                color: 'var(--itr-primary-text)',
              }}
            >
              <LockKeyhole size={22} />
            </div>
          )}
          <h1 style={{ fontSize: 21, letterSpacing: '-0.022em' }}>{branding.productName}</h1>
          {branding.tagline && (
            <p className="subtle" style={{ fontSize: 13.5, marginTop: 5 }}>
              {branding.tagline}
            </p>
          )}
        </div>

        {demo && !challenge && (
          <div className="card" style={{ padding: 18, marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 14 }}>
              <FlaskConical size={18} style={{ flexShrink: 0, marginTop: 2, color: 'var(--itr-primary)' }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>This is a live demo — choose who to sign in as</div>
                <div className="subtle" style={{ fontSize: 12.5, marginTop: 3, lineHeight: 1.45 }}>
                  A made-up IT provider with four clients and about 130 simulated devices. Each role sees what that
                  person would. Change things freely — it all goes back to how it was every{' '}
                  {demo.resetMinutes === 60 ? 'hour' : `${demo.resetMinutes} minutes`}.
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {demo.logins.map((choice) => (
                <button
                  key={choice.key}
                  type="button"
                  className="btn"
                  disabled={entering !== null || busy}
                  onClick={() => {
                    setEntering(choice.key);
                    setError(null);
                    void enterDemo(choice.key).finally(() => setEntering(null));
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', height: 'auto', padding: '10px 12px', textAlign: 'left', whiteSpace: 'normal' }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>
                      {choice.title}
                      <span className="subtle" style={{ fontWeight: 400 }}> · {choice.name}</span>
                    </span>
                    <span className="subtle" style={{ display: 'block', fontSize: 12, fontWeight: 400, lineHeight: 1.4, marginTop: 2 }}>
                      {choice.blurb}
                    </span>
                  </span>
                  {entering === choice.key ? <Loader2 size={15} className="spin" /> : <ArrowRight size={15} style={{ flexShrink: 0, opacity: 0.55 }} />}
                </button>
              ))}
            </div>
          </div>
        )}

        {!challenge ? (
          <form className="card" onSubmit={submit} style={{ padding: 22 }}>
            {needsServer && (
              <div className="field">
                <label className="field-label" htmlFor="server">
                  Server
                </label>
                <input
                  id="server"
                  className="input"
                  type="url"
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  value={serverUrl}
                  onChange={(e) => setServerUrl(e.target.value)}
                  placeholder="https://itr.example.com"
                />
              </div>
            )}

            <div className="field">
              <label className="field-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className="input"
                type="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>

            <div className="field">
              <label className="field-label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                className="input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>

            {error && <LoginError message={error} />}

            <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%', height: 38 }}>
              {busy ? <Loader2 size={15} className="spin" /> : null}
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : (
          <form className="card" onSubmit={submitCode} style={{ padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              {challenge.method === 'email' ? <Mail size={16} /> : <KeyRound size={16} />}
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {challenge.method === 'email' ? 'Check your email' : 'Enter your authenticator code'}
                </div>
                <div className="subtle" style={{ fontSize: 12 }}>
                  {challenge.method === 'email'
                    ? `We sent a code to ${challenge.sentTo ?? 'your email'}.`
                    : 'Open your authenticator app for the current 6-digit code.'}
                </div>
              </div>
            </div>

            <div className="field">
              <label className="field-label" htmlFor="otp">
                Code
              </label>
              <input
                id="otp"
                className="input"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={challenge.method === 'email' ? '123456' : '123456 — or a backup code'}
                style={{ letterSpacing: '0.08em', textAlign: 'center', fontSize: 18 }}
              />
            </div>

            {error && <LoginError message={error} />}

            <button className="btn btn-primary" type="submit" disabled={busy || !code.trim()} style={{ width: '100%', height: 38 }}>
              {busy ? <Loader2 size={15} className="spin" /> : null}
              {busy ? 'Verifying…' : 'Verify'}
            </button>

            <div className="row" style={{ justifyContent: 'space-between', marginTop: 14, fontSize: 12.5 }}>
              <LinkButton onClick={backToPassword} disabled={busy}>
                Back
              </LinkButton>
              {challenge.method === 'email' ? (
                <LinkButton onClick={() => switchMethod('email')} disabled={busy}>
                  Resend code
                </LinkButton>
              ) : (
                challenge.methods.includes('email') && (
                  <LinkButton onClick={() => switchMethod('email')} disabled={busy}>
                    Email me a code instead
                  </LinkButton>
                )
              )}
            </div>
          </form>
        )}

        {(branding.supportEmail || branding.supportPhone) && (
          <p className="subtle" style={{ textAlign: 'center', fontSize: 12, marginTop: 16 }}>
            Need help?{' '}
            {branding.supportEmail && <a href={`mailto:${branding.supportEmail}`}>{branding.supportEmail}</a>}
            {branding.supportEmail && branding.supportPhone && ' · '}
            {branding.supportPhone}
          </p>
        )}
      </div>

      <style>{`.spin { animation: spin 0.7s linear infinite; }`}</style>
    </div>
  );
}

function LoginError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        gap: 8,
        padding: '9px 11px',
        marginBottom: 14,
        borderRadius: 'var(--itr-radius-sm)',
        background: 'color-mix(in srgb, var(--itr-critical) 12%, transparent)',
        border: 'var(--itr-border-w) solid color-mix(in srgb, var(--itr-critical) 34%, transparent)',
        color: 'var(--itr-critical)',
        fontSize: 12.5,
      }}
    >
      {message}
    </div>
  );
}

function LinkButton({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'none',
        border: 0,
        padding: 0,
        font: 'inherit',
        color: 'var(--itr-primary)',
        textDecoration: 'underline',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}
